// Explore-stats page: Heads of state since Federation (#222).
// Sourced from the `heads_of_state` index (Parliamentary Handbook API):
// monarchs (sovereigns of Australia) + governors-general. Flagship: a
// chronological, sortable table of every head of state with term dates,
// tenure and the parliaments they spanned. Slices: GG tenure distribution,
// monarch reigns, and commencements per decade by role.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid, Box } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatStackedBar } from './charts';

function StatCardRow({ stats }) {
  if (!stats || stats.avg === undefined) return null;
  const items = [
    { label: 'Avg GG tenure', value: `${stats.avg} yrs` },
    { label: 'Longest', value: `${stats.longest} yrs` },
    { label: 'Shortest', value: `${stats.shortest} yrs` },
  ];
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {items.map((it) => (
        <Grid item xs={4} key={it.label}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5">{it.value}</Typography>
            <Typography variant="body2" color="text.secondary">{it.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

export default function HeadsOfState() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('heads-of-state');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Heads of state since Federation"
      description={`Australia's monarchs (${charts.monarch_count || 0}) and governors-general (${
        charts.gg_count || 0}) since 1901 — term dates, tenure, and the parliaments each served alongside.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="heads-of-state"
    >
      {data && (
        <>
          <StatCardRow stats={charts.gg_stats} />

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Governors-General by tenure</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Years in office, longest to shortest (incumbent measured to today).
            </Typography>
            <StatBar data={charts.gg_tenure || []} xKey="name" yKey="years"
                     layout="vertical" height={520} />
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Monarchs — reign as sovereign of Australia</Typography>
                <StatBar data={charts.monarch_reign || []} xKey="name" yKey="years"
                         layout="vertical" height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Commencements per decade</Typography>
                <Box>
                  <StatStackedBar data={charts.timeline || []} xKey="decade"
                                  keys={['Governors-General', 'Monarchs']} height={300} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </ExploreStatsPage>
  );
}
